import React, { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { LuSearch as CiSearch } from "react-icons/lu";
import { LuX as IoClose } from "react-icons/lu";

const ForwardSharePopup = ({ isOpen, onClose, onShare, groupId }) => {
    const { currentUser } = useUser();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("All");
    const [chats, setChats] = useState([]);
    const [groups, setGroups] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);

    // Reset states when popup opens/closes
    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            setSelectedItems([]);
            setSearchQuery("");
            setActiveTab("All");
        }
    }, [isOpen]);

    // Fetch user's friends/chats (people they can message)
    // Fetch user's chats (friends + users with message history)
    useEffect(() => {
        if (!currentUser?.uid || !isOpen) return;

        const usersRef = ref(database, "Users");
        const friendsRef = ref(database, `Users/${currentUser.uid}/friends`);

        const chatList = [];
        let processedCount = 0;
        let totalUsersToProcess = 0;

        const unsubscribe = onValue(usersRef, (snapshot) => {
            if (!snapshot.exists()) {
                setChats([]);
                setLoading(false);
                return;
            }

            const allUsersData = snapshot.val();
            const otherUsers = Object.entries(allUsersData)
                .filter(([uid]) => uid !== currentUser.uid);

            totalUsersToProcess = otherUsers.length;

            if (otherUsers.length === 0) {
                setChats([]);
                setLoading(false);
                return;
            }

            // First get friends list
            onValue(friendsRef, (friendsSnap) => {
                const friends = friendsSnap.val() || {};

                otherUsers.forEach(([uid, user]) => {
                    const isFriend = !!friends[uid];
                    const chatId = [currentUser.uid, uid].sort().join('_');
                    const chatRef = ref(database, `privateChats/${chatId}`);

                    // Check if there are any messages in this chat
                    onValue(chatRef, (chatSnap) => {
                        const hasMessages = chatSnap.exists();

                        // Include if: is friend OR has messages
                        if (isFriend || hasMessages) {
                            let lastMessage = null;
                            let messagePreview = "Say hi 👋";

                            if (hasMessages) {
                                const messages = Object.entries(chatSnap.val())
                                    .map(([id, msg]) => ({ id, ...msg }))
                                    .sort((a, b) => b.timestamp - a.timestamp);

                                lastMessage = messages[0];

                                if (lastMessage) {
                                    if (lastMessage.text) messagePreview = lastMessage.text;
                                    else if (lastMessage.imageUrl) messagePreview = "📷 Photo";
                                    else if (lastMessage.audioUrl) messagePreview = "🎤 Voice message";
                                    else messagePreview = "Message";
                                }
                            }

                            chatList.push({
                                id: uid,
                                type: 'chat',
                                name: user.name || user.firstName || "Unknown",
                                photo: user.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef",
                                subtitle: messagePreview,
                                lastTimestamp: lastMessage?.timestamp || 0,
                                isFriend: isFriend
                            });
                        }

                        processedCount++;
                        if (processedCount === totalUsersToProcess) {
                            // Sort: friends first, then by most recent message
                            const sortedChats = chatList.sort((a, b) => {
                                if (a.isFriend && !b.isFriend) return -1;
                                if (!a.isFriend && b.isFriend) return 1;
                                return b.lastTimestamp - a.lastTimestamp;
                            });

                            setChats(sortedChats);
                            setLoading(false);
                        }
                    }, { once: true });
                });
            }, { once: true });
        });

        return () => off(usersRef);
    }, [currentUser, isOpen]);

    // Fetch user's groups
    useEffect(() => {
        if (!currentUser?.uid || !isOpen) return;

        const groupsRef = ref(database, "Groups");

        const unsubscribeGroups = onValue(groupsRef, (snapshot) => {
            if (!snapshot.exists()) {
                setGroups([]);
                return;
            }

            const data = snapshot.val();
            const groupList = [];

            Object.entries(data).forEach(([gId, group]) => {
                // Check if user is member or host
                const isMember = group.members && group.members[currentUser.uid];
                const isHost = group.hostId === currentUser.uid;

                if ((isMember || isHost) && gId !== groupId) { // Don't include current group
                    groupList.push({
                        id: gId,
                        type: 'group',
                        name: group.name,
                        photo: group.photo || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346",
                        memberCount: group.members ? Object.keys(group.members).length : 1
                    });
                }
            });

            setGroups(groupList.sort((a, b) => a.name.localeCompare(b.name)));
        });

        return () => unsubscribeGroups();
    }, [currentUser, groupId, isOpen]);

    // Fetch user's hosted communities
    useEffect(() => {
        if (!currentUser?.uid || !isOpen) return;

        const communitiesRef = ref(database, "Communities");

        const unsubscribe = onValue(communitiesRef, (snapshot) => {
            const data = snapshot.val() || {};

            // Filter only communities where current user is the host/owner
            const ownedCommunities = Object.entries(data)
                .filter(([_, community]) => community.hostId === currentUser.uid) // Only communities where current user is host
                .map(([id, community]) => ({
                    id,
                    type: 'community',
                    name: community.name,
                    photo: community.imageUrl || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346',
                    description: community.description || "",
                    memberCount: community.members ? Object.keys(community.members).length : 1,
                    hostId: community.hostId // Include hostId for reference
                }))
                .filter(c => c.id !== groupId); // Don't include current group

            setCommunities(ownedCommunities.sort((a, b) => a.name.localeCompare(b.name)));
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, groupId, isOpen]);

    const handleSelectItem = (item) => {
        setSelectedItems(prev => {
            const exists = prev.find(i => i.id === item.id && i.type === item.type);
            if (exists) {
                return prev.filter(i => !(i.id === item.id && i.type === item.type));
            } else {
                return [...prev, item];
            }
        });
    };

    const handleShare = () => {
        if (selectedItems.length > 0) {
            onShare(selectedItems);
            onClose();
        }
    };

    const getFilteredItems = () => {
        let items = [];

        if (activeTab === "Chats") {
            items = chats;
        } else if (activeTab === "Groups") {
            items = groups;
        } else if (activeTab === "Communities") {
            items = communities;
        } else {
            // "All" tab - combine all
            items = [...chats, ...groups, ...communities];
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            items = items.filter(item =>
                item.name.toLowerCase().includes(query)
            );
        }

        return items;
    };

    if (!isOpen) return null;

    const filteredItems = getFilteredItems();
    const isLoading = loading && chats.length === 0 && groups.length === 0 && communities.length === 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white dark:bg-[#23272c] w-full sm:w-[480px] sm:rounded-lg min-h-[70vh] max-h-[70vh] md:min-h-[85vh] md:max-h-[85vh] flex flex-col rounded-t-xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b dark:border-[#363e46] border-[#D9D9D9]/50">
                    <h3 className="text-lg font-semibold dark:text-white">Share to</h3>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
                    >
                        <IoClose className="w-6 h-6 dark:text-white" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full rounded-xl pl-10 pr-4 py-2.5 bg-[#F5F9FE] dark:bg-[#2A2A2A] outline-none dark:text-white border border-gray-200 dark:border-none"
                        />
                        <CiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex px-4 gap-2 mb-2 pb-1">
                    {['All', 'Chats', 'Groups', 'Communities'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-3 py-1.5 text-xs md:text-sm rounded-full whitespace-nowrap transition ${activeTab === tab
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300'
                                }`}
                        >
                            {tab} {tab === 'Chats'}
                            {tab === 'Groups'}
                            {tab === 'Communities'}
                        </button>
                    ))}
                </div>

                {/* Items List */}
                <div className="flex-1 overflow-y-auto p-2">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No {activeTab.toLowerCase()} found
                        </div>
                    ) : (
                        filteredItems.map((item) => (
                            <div
                                key={`${item.type}-${item.id}`}
                                onClick={() => handleSelectItem(item)}
                                className={`flex items-center p-3 rounded-lg cursor-pointer transition ${selectedItems.find(i => i.id === item.id && i.type === item.type)
                                    ? 'bg-primary/10 dark:bg-primary/20'
                                    : 'hover:bg-gray-100 dark:hover:bg-[#2A2A2A]'
                                    }`}
                            >
                                <div className="relative">
                                    <img
                                        src={item.photo}
                                        alt={item.name}
                                        className="w-12 h-12 rounded-full object-cover"
                                        onError={(e) => {
                                            e.target.src = "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef";
                                        }}
                                    />
                                    {item.type === 'group' && (
                                        <div className="absolute -bottom-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            G
                                        </div>
                                    )}
                                    {item.type === 'community' && (
                                        <div className="absolute -bottom-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            C
                                        </div>
                                    )}
                                </div>

                                <div className="ml-3 flex-1 min-w-0">
                                    <h4 className="font-medium dark:text-white truncate">
                                        {item.name}
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {item.type === 'group' && `${item.memberCount || 0} members`}
                                        {item.type === 'community' && (item.description || `${item.memberCount || 0} members`)}
                                        {item.type === 'chat' && 'Chat'}
                                    </p>
                                </div>

                                {/* Selection indicator */}
                                <div className={`w-5 h-5 rounded-full border-2 ml-2 flex items-center justify-center ${selectedItems.find(i => i.id === item.id && i.type === item.type)
                                    ? 'bg-primary border-primary'
                                    : 'border-gray-300 dark:border-gray-600'
                                    }`}>
                                    {selectedItems.find(i => i.id === item.id && i.type === item.type) && (
                                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer with selected count and share button */}
                {selectedItems.length > 0 && (
                    <div className="p-4 border-t dark:border-[#363e46] border-[#D9D9D9]/50">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {selectedItems.length} selected
                            </span>
                            <button
                                onClick={handleShare}
                                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
                            >
                                Share {selectedItems.length > 1 ? `(${selectedItems.length})` : ''}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForwardSharePopup;